import { createSlice, createAsyncThunk, PayloadAction, AnyAction } from '@reduxjs/toolkit'

import { FlashingState, UserLocationsTyping, FetchLocationListPayload, SubmitLocationType } from './flashingTypings'
import {
    deleteEmptyLocation,
    getFlashingLocation,
    getFlashingLocationMaterials,
    getGeoLocationList,
    postSubmitLocation
} from '../../api/flashing'

const initialState: FlashingState = {
    list: [],
    sortedList: [],
    sortedLoading: false,
    sortedHasMore: true,
    visitedGeo: false,
    location: null,
    loadingLocation: false,
    loading: false,
    pendingLocation: null,
    pendingLocationErrorText: null,
    materials_loading: false,
    materials_has_more: true,
    chosenLocationId: null
}

export const fetchGeoLocationList = createAsyncThunk('fetchGeoLocationList', async (params: UserLocationsTyping) => {
    return await getGeoLocationList(params)
})

export const fetchSortedLocationList = createAsyncThunk(
    'fetchSortedLocationList',
    async (params: UserLocationsTyping) => {
        return await getGeoLocationList(params)
    }
)

export const fetchFlashingLocation = createAsyncThunk('fetchFlashingLocation', async (params: { id: number }) => {
    return await getFlashingLocation(params)
})

export const fetchFlashingLocationMaterials = createAsyncThunk(
    'fetchFlashingLocationMaterials',
    async (params: { id: number; limit: number; offset: number }) => {
        return await getFlashingLocationMaterials(params)
    }
)

export const deleteLocationThunk = createAsyncThunk('deleteLocationThunk', async (params: { id: number }) => {
    return await deleteEmptyLocation(params)
})

export const submitNewLocationThunk = createAsyncThunk(
    'submitNewLocationThunk',
    async (params: SubmitLocationType, { rejectWithValue }) => {
        try {
            const response = await postSubmitLocation(params)
            return response
        } catch (err: any) {
            if (err && err.status === 'error') {
                return rejectWithValue(err)
            }

            return rejectWithValue({
                status: 'error',
                message: err.message || 'Unknown error'
            })
        }
    }
)

const flashingSlice = createSlice({
    name: 'flashing',
    initialState,
    reducers: {
        clearCurrentLocation(state) {
            state.location = null
        },
        clearMaterialList(state) {
            if (state.location && state.location.materials) state.location.materials = []
        },
        clearSortedList(state) {
            state.sortedList = []
            state.sortedHasMore = true
        },
        setChosenLocationId(state, action: PayloadAction<number | null>) {
            state.chosenLocationId = action.payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchFlashingLocationMaterials.fulfilled, (state, action: AnyAction) => {
            state.materials_loading = false
            const incoming = action.payload.list
            state.location.materials = state.location.materials
                .concat(incoming)
                .filter((item, index, self) => index === self.findIndex((obj) => obj.id === item.id))
            if (incoming.length < action.meta.arg.limit) {
                state.materials_has_more = false
            }
        })

        builder.addCase(fetchFlashingLocationMaterials.pending, (state) => {
            state.materials_loading = true
        })

        builder.addCase(fetchFlashingLocationMaterials.rejected, (state) => {
            state.materials_loading = false
        })

        builder.addCase(fetchFlashingLocation.pending, (state) => {
            state.loadingLocation = true
            state.location = null
        })

        builder.addCase(fetchFlashingLocation.fulfilled, (state, action: AnyAction) => {
            state.loadingLocation = false
            state.location = action.payload.location
            state.location.materials = []
            state.materials_has_more = true
        })

        builder.addCase(fetchFlashingLocation.rejected, (state) => {
            state.loadingLocation = false
            state.materials_has_more = false
        })

        builder.addCase(fetchGeoLocationList.pending, (state) => {
            state.loading = true
        })
        builder.addCase(fetchGeoLocationList.fulfilled, (state, action: PayloadAction<FetchLocationListPayload>) => {
            state.loading = false

            const newList = action.payload.data
            const oldIds = new Set(state.list.map((item) => item.id))
            const newIds = new Set(newList.map((item) => item.id))

            const isSame = oldIds.size === newIds.size && newList.every((item) => oldIds.has(item.id))

            if (!isSame) {
                state.list = newList
            }
        })
        builder.addCase(fetchGeoLocationList.rejected, (state) => {
            state.loading = false
        })

        builder.addCase(fetchSortedLocationList.pending, (state) => {
            state.sortedLoading = true
        })
        builder.addCase(fetchSortedLocationList.fulfilled, (state, action: PayloadAction<FetchLocationListPayload>) => {
            state.sortedLoading = false
            const incoming = action.payload.data
            if (incoming.length < 100) {
                state.sortedHasMore = false
            }
            if (incoming.length > 0 && state.sortedList.some((item) => item.id === incoming[0].id)) {
                state.sortedHasMore = false
                return
            }
            state.sortedList = state.sortedList
                .concat(incoming)
                .filter((item, index, self) => index === self.findIndex((obj) => obj.id === item.id))
        })
        builder.addCase(fetchSortedLocationList.rejected, (state) => {
            state.sortedLoading = false
        })

        builder.addCase(submitNewLocationThunk.pending, (state) => {
            state.loading = true
        })
        builder.addCase(submitNewLocationThunk.fulfilled, (state, action: AnyAction) => {
            state.loading = false
            state.list.push(action.payload.location)
        })
        builder.addCase(submitNewLocationThunk.rejected, (state, action: AnyAction) => {
            state.pendingLocation = action.payload.data
            state.pendingLocationErrorText = action.payload.message
            state.loading = false
        })

        builder.addCase(deleteLocationThunk.pending, (state) => {
            state.loading = true
        })
        builder.addCase(deleteLocationThunk.fulfilled, (state, action: AnyAction) => {
            state.loading = false
            state.list = state.list.filter((location) => location.id !== action.meta.arg.id)
            state.pendingLocation = null
        })
        builder.addCase(deleteLocationThunk.rejected, (state) => {
            state.loading = false
        })
    }
})

export const { clearCurrentLocation, clearMaterialList, clearSortedList, setChosenLocationId } = flashingSlice.actions
export default flashingSlice.reducer
